"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import type { OrgContext as OrgContextType } from "shared";

import { fetchCurrentOrg } from "../data/orgs";

export const OrgContext = createContext<OrgContextType | undefined>(undefined);

interface OrgProviderProps {
  children: React.ReactNode;
}

/**
 * Organization Provider Component
 *
 * Resolves the current organization based on the domain (hostname) and provides
 * it via React Context to all child components.
 *
 * Domain Resolution Flow:
 * 1. Get current hostname (e.g., "ohc400.org", "localhost")
 * 2. Query Firestore for org where domains array contains the hostname
 * 3. Verify org is active
 * 4. Store org data in context state
 *
 * @example
 * // In your root layout:
 * <OrgProvider>
 *   <App />
 * </OrgProvider>
 */
export function OrgProvider({ children }: OrgProviderProps) {
  const [orgContext, setOrgContext] = useState<OrgContextType>({
    org: null,
    orgUid: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    async function loadOrg() {
      try {
        // Fetch the org (server-side bootstrap should have already created it)
        const result = await fetchCurrentOrg();

        if (!result) {
          throw new Error(
            `No organization found for domain: ${window.location.hostname}`,
          );
        }

        setOrgContext({
          org: result.org,
          orgUid: result.orgUid,
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error("[OrgContext] Error loading organization:", error);
        setOrgContext({
          org: null,
          orgUid: null,
          loading: false,
          error: error as Error,
        });
      }
    }

    loadOrg();
  }, []);

  return (
    <OrgContext.Provider value={orgContext}>{children}</OrgContext.Provider>
  );
}

/**
 * Hook to access the organization context
 *
 * Returns the full context including loading and error states.
 * Use this when you need to handle loading/error states in your component.
 *
 * @throws Error if used outside of OrgProvider
 *
 * @example
 * const { org, orgUid, loading, error } = useOrgContext();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!org) return null;
 *
 * return <div>{org.name}</div>;
 */
export function useOrgContext() {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error("useOrgContext must be used within an OrgProvider");
  }
  return context;
}
